import React, { useEffect, useState } from 'react';
import { ArrowRight, TrendingUp, Globe2, Package, Search, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { getHomepageSectionML } from '../../services/api';

// Icon mapping
const iconMap = {
  TrendingUp,
  Globe2,
  Package,
  Search,
  Users,
  'globe': Globe2,
  'shield': Users,
  'clock': TrendingUp
};

const HighlightSectionML = ({ sectionName = 'highlight_trading' }) => {
  const { language } = useLanguage();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHighlightContent();
  }, [language, sectionName]);

  const loadHighlightContent = async () => {
    setLoading(true);
    const normalizedLang = ['pt','en','fr','ar','zh'].includes(language) ? language : (language.startsWith('pt') ? 'pt' : 'en');
    const result = await getHomepageSectionML(sectionName, normalizedLang);
    if (result.success) {
      setContent(result.data);
    }
    setLoading(false);
  };

  // Don't render if not enabled or no data
  if (loading) {
    return null;
  }

  if (!content || !content.enabled) {
    return null;
  }

  return (
    <section className="relative py-20 overflow-hidden bg-[#0a0a0a]">
      {/* Background Image if available */}
      {content.background_image && (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${content.background_image})`,
              filter: `brightness(${(content.image_brightness || 100) / 100})`
            }}
          />
          <div
            className="absolute inset-0"
            style={{ backgroundColor: `rgba(0, 0, 0, ${(content.overlay_opacity || 40) / 100})` }}
          />
        </>
      )}
      {/* Fallback gradient when no image */}
      {!content.background_image && (
        <>
          <div className="absolute inset-0 bg-gradient-to-br from-[#023E8A] via-[#0077B6] to-[#00B4D8]" />
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }} />
          </div>
        </>
      )}

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Badge */}
          <div className="text-center mb-6">
            <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-sm font-semibold text-white uppercase tracking-wider">
              {content.badge}
            </span>
          </div>

          {/* Main Content */}
          <div className="text-center text-white mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              {content.title}
            </h2>
            <p className="text-2xl md:text-3xl font-semibold text-white/90 mb-6">
              {content.subtitle}
            </p>
            <p className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto leading-relaxed">
              {content.description}
            </p>
          </div>

          {/* Pillars Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {content.pillars && content.pillars.map((pillar, index) => {
              const IconComponent = iconMap[pillar.icon] || Package;
              
              return (
                <div
                  key={index}
                  className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6 hover:bg-white/20 transition-all duration-300"
                >
                  <div className="flex items-center justify-center w-12 h-12 bg-white/20 rounded-lg mb-4">
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {pillar.title}
                  </h3>
                  <p className="text-white/80 text-sm">
                    {pillar.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {content.cta_primary && (
              <Link to={content.cta_primary.link}>
                <Button
                  size="lg"
                  className="bg-white text-[#023E8A] hover:bg-white/90 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-base px-8 py-6 font-semibold"
                >
                  {content.cta_primary.text}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            )}
            {content.cta_secondary && (
              <Link to={content.cta_secondary.link}>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 backdrop-blur-sm transition-all duration-300 text-base px-8 py-6 font-semibold"
                >
                  {content.cta_secondary.text}
                </Button>
              </Link>
            )}
          </div>

          {/* Trust Indicator */}
          {content.trust_note && (
            <div className="text-center mt-8">
              <p className="text-sm text-white/70">
                {content.trust_note}
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default HighlightSectionML;
